import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { qk } from "../../lib/queryKeys";
import { getJson } from "../../lib/http";
import { useCompanyIntegratorsStore } from "../../store/admin/companyIntegratorsStore";
import type { RetransmissionOption } from "../../store/admin/companyIntegratorsStore";
import type { Integrator } from "../../store/reseller-admin/integratorsStore";

interface RetransmissionScope {
  type?: "all" | "devices" | "group";
  device_ids?: string[];
  group_id?: string | null;
}

interface RetransmissionCredentials {
  ws_name?: string;
  customer_name?: string;
  customer_id?: string;
}

interface Retransmission {
  id: string;
  integrator_id: string | null;
  active: boolean;
  scope?: RetransmissionScope;
  integrator_credentials?: RetransmissionCredentials;
}

export interface CompanyIntegratorsResult {
  integrators: Integrator[];
  retransmissions: RetransmissionOption[];
  hasRetransmissions: boolean;
  coversAllDevices: boolean;
  coveredDeviceIds: Set<string>;
}

async function fetchCompanyIntegratorsData(
  companyId: string,
  signal?: AbortSignal,
): Promise<CompanyIntegratorsResult> {
  const [retransmissions, catalog] = await Promise.all([
    getJson<{ data?: Retransmission[] }>(
      "/companyRetransmissionsList",
      { company_id: companyId, limit: 200, page: 1, active: "true" },
      { signal },
    ).then((b) => b.data ?? []),
    getJson<{ data?: Integrator[] }>(
      "/integratorList",
      { limit: 200, page: 1 },
      { signal },
    ).then((b) => b.data ?? []),
  ]);

  const catalogById = new Map(catalog.map((it) => [it.id, it]));
  const seen = new Set<string>();
  const integrators: Integrator[] = [];
  const retransmissionOptions: RetransmissionOption[] = [];
  let coversAllDevices = false;
  const coveredDeviceIds = new Set<string>();
  let activeCount = 0;

  for (const r of retransmissions) {
    if (r.active === false) continue;
    activeCount += 1;

    if (!r.scope || r.scope.type === "all") {
      coversAllDevices = true;
    } else {
      for (const d of r.scope.device_ids || []) coveredDeviceIds.add(d);
    }

    const it = r.integrator_id ? catalogById.get(r.integrator_id) : undefined;
    const wsName = r.integrator_credentials?.ws_name || "";
    const customerName = r.integrator_credentials?.customer_name || "";
    const rCoversAll = !r.scope || r.scope.type === "all";
    retransmissionOptions.push({
      id: r.id,
      integrator_id: r.integrator_id ?? null,
      adapter: it?.adapter ?? null,
      ws_name: wsName,
      customer_name: customerName,
      label: wsName || it?.name || r.id,
      coversAllDevices: rCoversAll,
      device_ids: rCoversAll ? [] : r.scope?.device_ids || [],
    });

    if (!it || !it.adapter || seen.has(it.adapter)) continue;
    seen.add(it.adapter);
    integrators.push(it);
  }

  return {
    integrators,
    retransmissions: retransmissionOptions,
    hasRetransmissions: activeCount > 0,
    coversAllDevices,
    coveredDeviceIds,
  };
}

// Write-through: deja el resultado en `useCompanyIntegratorsStore` para que los
// consumidores que leen del store (VehicleCard, VehicleEvidenceModal,
// IntegradoresMonitor) sigan funcionando sin cambios.
export function useCompanyIntegrators(companyId: string | null) {
  const query = useQuery({
    queryKey: qk.companyIntegrators(companyId),
    queryFn: ({ signal }) => fetchCompanyIntegratorsData(companyId as string, signal),
    enabled: !!companyId,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (query.data) {
      useCompanyIntegratorsStore.setState({
        ...query.data,
        loadedCompanyId: companyId,
        loading: false,
      });
    }
  }, [query.data, companyId]);

  return query;
}
